import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { billingAPI } from '../services/api';

const accessColor = (level) => {
  if (level === 'full') return 'success';
  if (level === 'preview') return 'warning';
  return 'default';
};

function Pricing() {
  const { t, i18n } = useTranslation();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    setLoading(true);
    billingAPI
      .getPlans({ headers: { 'Accept-Language': i18n.language } })
      .then((response) => {
        if (!active) return;
        setPlans(response.data?.plans || []);
        setError('');
      })
      .catch(() => {
        if (!active) return;
        setError(t('pricing.loadFailed', 'Unable to load plans.'));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [i18n.language, t]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        {t('pricing.title', 'Plans')}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        {t('pricing.subtitle', 'Compare access by plan. Checkout coming in a later release.')}
      </Typography>
      <Grid container spacing={2}>
        {plans.map((plan) => (
          <Grid item xs={12} md={4} key={plan.code}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Stack spacing={2}>
                  <Typography variant="h6">{plan.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {plan.code}
                  </Typography>
                  <Stack spacing={1}>
                    {plan.features.map((feature) => (
                      <Box
                        key={`${plan.code}-${feature.feature_key}`}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          gap: 1,
                          alignItems: 'flex-start',
                        }}
                      >
                        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                          {feature.feature_key}
                        </Typography>
                        <Chip
                          size="small"
                          label={feature.access_label}
                          color={accessColor(feature.access_level)}
                        />
                      </Box>
                    ))}
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

export default Pricing;
